import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {supabase} from "./utils/supabaseClient";
import {Box, Button, Center, FormControl, FormLabel, Heading, Badge,Select, Stack, Text, HStack, Divider, Card,Link, useToast, Spinner, Container,VStack} from "@chakra-ui/react";
import {CheckCircleIcon} from "@chakra-ui/icons";
import { FiFile, FiLink, FiArrowRight, FiUpload, FiArrowLeft } from "react-icons/fi";
import {saveAs} from "file-saver";
import {motion} from "framer-motion";
import moment from "moment";

export const EachAssessment = () => {
  const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)
    const toast = useToast()
    const {assessmentID} = useParams();
    const [selectedFiles, setSelectedFiles] = useState({});
    const [assessmentTemplate, setAssessmentTemplate] = useState(null);
    const [existingAssessment, setExistingAssessment] = useState(null);
    const [loading, setLoading] = useState(true);

    const getAssessmentDetails = async () => {
        try {
            // Get assessment template details
            const { data: templateData, error: templateError } = await supabase
                .from("assessment_template_per_registration_id")
                .select("*")
                .eq("registration_id", assessmentID)
            console.log(templateData)
            if (templateError) throw templateError;
            setAssessmentTemplate(templateData);

            // Check if assessment already exists for this registration and template
            const { data: assessmentData, error: assessmentError } = await supabase
                .from("assessments")
                .select("*")
                .eq("registration_id", assessmentID)

            if (assessmentError && assessmentError.code !== 'PGRST116') {
                // PGRST116 is "no rows returned" error, which is expected if no assessment exists
                throw assessmentError;
            }

            if (assessmentData) {
                setExistingAssessment(assessmentData);
            }

        } catch (error) {
            console.error("Error fetching assessment details:", error);
            toast({
                title: "Error",
                description: "Could not load assessment details.",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getAssessmentDetails();
    }, [assessmentID]);

    if (loading) {
        return (
            <Center minH="100vh">
                <Spinner size="xl" />
            </Center>
        );
    }

    if (!assessmentTemplate) {
        return (
            <Center minH="100vh">
                <Text>No assessment template found.</Text>
            </Center>
        );
    }

    const downloadFile = async (filePath, bucketName="Course Files") => { 
        setIsDownloading(true)
        try {
            const { data, error } = await supabase.storage.from(bucketName).download(filePath);
            if (error) throw error;
    
            const fileName = filePath.split("/").pop();
            saveAs(data, fileName);
            
            // Insert a record into the "activity" table upon successful download
            const { error: activityError } = await supabase
            .from("activity")
            .insert({
                created_at: new Date().toISOString(),
                registration_id: assessmentID,
                action: "Download",
                resource: fileName,
            });

        if (activityError) {
            console.error("Error logging download activity:", activityError);
        }
        } catch (error) {
            console.error(`Error downloading file from ${bucketName}:`, error);
        } finally {
            setIsDownloading(false);
        }
    };

    const handleFileUpload = async (file, assessment_template_id, assessment_id) => {
        console.log(assessment_template_id)
        setIsSubmitting(true)

        try {
            const path = `${assessmentID}/${assessment_template_id}/${file.name}`;
            const { data, error } = await supabase.storage.from("Assessment Files").upload(path, file, {
                upsert: true,
            });

            if (error) {
                throw error;
            }

            // Insert a record into the "activity" table upon successful upload
            const { error: activityError } = await supabase
            .from("activity")
            .insert({
                created_at: new Date().toISOString(),
                registration_id: assessmentID,
                action: "Upload",
                resource: file.name,
            });
            
            if (activityError) {
                console.error("Error logging upload activity:", activityError);
            }

            // Upsert existing assessment
            if (assessment_id) {
            const { data: updateData, error: updateError } = await supabase
              .from("assessments")
              .upsert({
                  assessment_id: assessment_id,
                  registration_id: assessmentID,
                  assessment_template_id: assessment_template_id,
                  submitted_on: new Date().toISOString(),
                  uploaded_file_name: file.name,
              });

              if (updateError) {
                    throw updateError;
                }
            } else {
              const { data: insertData, error: insertError } = await supabase
              .from("assessments")
              .insert({
                  registration_id: assessmentID,
                  assessment_template_id: assessment_template_id,
                  submitted_on: new Date().toISOString(),
                  uploaded_file_name: file.name,
              });

              if (insertError) {
                throw insertError;
              }
            }

            toast({
                title: existingAssessment ? "Update successful" : "Upload successful",
                description: `Your assessment has been ${existingAssessment ? 'updated' : 'uploaded'}.`,
                status: "success",
                duration: 3000,
                isClosable: true,
            });

            // Refresh assessment data
            getAssessmentDetails();

            // Clear the file input for this specific assessment
            setSelectedFiles(prev => {
                const newState = { ...prev };
                delete newState[assessment_template_id];
                return newState;
            });
            document.getElementById(`file-upload-${assessment_template_id}`).value = "";

        } catch (error) {
            console.error("Error uploading file:", error);
            toast({
                title: "Upload failed",
                description: error.message,
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box minH="100vh" py={8}>
            <Container>
              <Button variant="outline" mb={5} leftIcon={<FiArrowLeft />} onClick={() => navigate("/enrolled-courses")}>
                Back To Course
              </Button>
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                  {assessmentTemplate.map(eachAssessment => {
                    return (
                      <Box
                      bg="white"
                      p={8}
                      borderRadius="lg"
                      boxShadow="md"
                      >
                      <Heading
                          size="sm"
                          mb={6}
                          pb={2}
                      >
                          {eachAssessment?.assessment_items?.name}
                      </Heading>

                                             {eachAssessment.uploaded_file_name && (
                            <Card
                                p={5}
                                bg="green.50"
                                mb={5}
                            >
                                <Heading size="xs" mb={2} color="green.700">
                                    Your Previous Submission
                                </Heading>
                                <Text mb={2}>
                                    File Name: <strong>{eachAssessment.uploaded_file_name}</strong>
                                </Text>
                                <Button
                                    isLoading={isDownloading}
                                    leftIcon={<FiFile />}
                                    onClick={() => downloadFile(`${assessmentID}/${eachAssessment.assessment_template_id}/${eachAssessment.uploaded_file_name}`,"Assessment Files")}
                                >
                                    Download Your Previous Submission
                                </Button>
                                <Text mt={2} fontSize="sm">
                                    Submitted on: {moment(eachAssessment.submitted_on).format("DD MMM YYYY, h:mm A")}
                                </Text>
                            </Card>
                       )}

                       <Card
                           p={5}
                           
                           mb={5}
                       >
                           <VStack align="stretch">
                               <HStack>
                                   <Text >
                                       {eachAssessment?.assessment_items?.name}
                                   </Text>
                                   <Badge
                                       colorScheme={eachAssessment?.submitted_on ? "green" : "red"}
                                   >
                                       {eachAssessment?.submitted_on ? "Submitted" : "Not Submitted"}
                                   </Badge>
                               </HStack>
                               <Box mt={5}>
                                   <input
                                       id={`file-upload-${eachAssessment.assessment_template_id}`}
                                       type="file"
                                       onChange={(e) => setSelectedFiles(prev => ({
                                           ...prev,
                                           [eachAssessment.assessment_template_id]: e.target.files[0]
                                       }))}
                                       style={{ marginBottom: "10px" }}
                                   />
                                   <Button
                                       isLoading={isSubmitting}
                                       leftIcon={<FiUpload />}
                                       onClick={() => handleFileUpload(selectedFiles[eachAssessment.assessment_template_id], eachAssessment.assessment_template_id, eachAssessment.assessment_id)}
                                       isDisabled={!selectedFiles[eachAssessment.assessment_template_id]}
                                   >
                                       {eachAssessment?.submitted_on ? "Update Submission" : "Upload Submission"}
                                   </Button>
                               </Box>
                           </VStack>
                       </Card>

                        {eachAssessment?.assessment_items?.resources?.map((section, sectionindex) => 
                          section?.items?.map((item, index) => {
                            if (item.type === "text") {
                              return (
                                  <Text key={index} mb={5}>
                                      {item.item_name}
                                  </Text>
                              );
                            } else if (item.type === "file") {
                                return (
                                    <Card
                                        key={index}
                                        p={5}
                                        
                                        mb={5}
                                    >
                                        <HStack>
                                            <FiFile  />
                                            <Link
                                                as="span"
                                                onClick={() => downloadFile(item.item_name)}
                                                textDecoration="underline"
                                                cursor="pointer"
                                                style={{overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                                            >
                                                {item.item_name}
                                            </Link>
                                        </HStack>
                                    </Card>
                                );
                            } else if (item.type === "link") {
                                return (
                                    <Card
                                        key={index}
                                        p={5}
                                        mb={5}
                                    >
                                      <HStack>
                                        <FiLink />
                                        <Link
                                            as="span"
                                            href={item.item_name}
                                            textDecoration="underline"
                                            cursor="pointer"
                                            className="x-overflow-hidden"
                                            >
                                                {item.item_name}
                                            </Link>
                                        </HStack>
                                    </Card>
                                );
                            }
                          })
                        )}
                    
                      </Box>
                    )
                  })}
                    
                </motion.div>
            </Container>
        </Box>
    )
}
